/**
 * Activity entity: backend DTOs, API calls, and the DTO -> display mapper.
 *
 * The feed is human actions only (the backend writes rows as a side-effect of
 * uploads/confirms/case work — DATA-MODEL.md ActivityEvent); this module only
 * ever reads it.
 */

import type { ActivityItem } from '../../app/types';
import { apiGet } from '../apiClient';
import { formatDate } from './format';

/** What the activity was about; the icon derives from `kind`. */
export interface ActivitySubjectDto {
  /** Open set: document | case | employee | assertion | conversation | ... */
  kind: string;
  id: string;
  label: string;
}

export interface ActivityDto {
  id: string;
  /** ISO timestamp of the action. */
  at: string;
  /** Display attribution ("Priya", "You"). */
  actorLabel: string;
  /** Open verb set: uploaded | confirmed | resolved | opened | added | ... */
  verb: string;
  subject: ActivitySubjectDto;
}

/** GET /api/activity?companyId={id} — newest first, capped server-side. */
export function listActivity(companyId: string): Promise<ActivityDto[]> {
  return apiGet<ActivityDto[]>(`/api/activity?companyId=${encodeURIComponent(companyId)}`);
}

/** Subject kind -> feed icon. Unknown kinds read as documents (the safe default). */
const KIND_TO_ICON: Record<string, ActivityItem['icon']> = {
  document: 'doc',
  case: 'case',
  conversation: 'chat',
  employee: 'person',
  assertion: 'shield',
};

/** ActivityDto -> display ActivityItem ("You uploaded Staff handbook."). */
export function mapActivity(dto: ActivityDto): ActivityItem {
  return {
    id: dto.id,
    icon: KIND_TO_ICON[dto.subject.kind] ?? 'doc',
    text: `${dto.actorLabel} ${dto.verb} ${dto.subject.label}.`,
    when: formatDate(dto.at),
  };
}
